// WebAuthn Utilities
// Provides core functionality for passkey operations

import type {
  WebAuthnChallenge,
  WebAuthnSupport,
  WebAuthnError,
  WebAuthnErrorType,
  PasskeyRegistrationRequest,
  PasskeyRegistrationResponse,
  PasskeyAuthenticationRequest,
  PasskeyAuthenticationResponse,
  VerificationResult,
  Base64URLString,
  PublicKeyCredentialCreationOptionsExtended,
  PublicKeyCredentialRequestOptionsExtended
} from '../types/webauthn';

// Base64URL encoding/decoding utilities
export class Base64URL {
  /**
   * Encode ArrayBuffer to Base64URL string
   */
  static encode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Decode Base64URL string to ArrayBuffer
   */
  static decode(base64url: string): ArrayBuffer {
    // Add padding if needed
    let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) {
      base64 += '=';
    }
    
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Convert string to ArrayBuffer
   */
  static fromString(str: string): ArrayBuffer {
    return new TextEncoder().encode(str).buffer;
  }

  /**
   * Convert ArrayBuffer to string
   */
  static toString(buffer: ArrayBuffer): string {
    return new TextDecoder().decode(buffer);
  }
}

// Challenge generation and validation
export class ChallengeManager {
  private static readonly CHALLENGE_LENGTH = 32;
  private static readonly DEFAULT_TIMEOUT = 300000; // 5 minutes

  /**
   * Generate a cryptographically secure random challenge
   */
  static generateChallenge(): ArrayBuffer {
    return crypto.getRandomValues(new Uint8Array(this.CHALLENGE_LENGTH)).buffer;
  }

  /**
   * Create a challenge object with metadata
   */
  static createChallenge(rpId: string, origin: string, userId?: string): WebAuthnChallenge {
    const challenge = Base64URL.encode(this.generateChallenge());
    const now = new Date();
    const expiresAt = new Date(now.getTime() + this.DEFAULT_TIMEOUT);

    return {
      challenge,
      timeout: this.DEFAULT_TIMEOUT,
      rp_id: rpId,
      origin,
      user_id: userId,
      created_at: now.toISOString(),
      expires_at: expiresAt.toISOString()
    };
  }

  /**
   * Validate if a challenge is still valid
   */
  static isValidChallenge(challenge: WebAuthnChallenge): boolean {
    const now = new Date();
    const expiresAt = new Date(challenge.expires_at);
    return now <= expiresAt;
  }
}

// Browser support detection
export class WebAuthnDetector {
  /**
   * Check comprehensive WebAuthn support
   */
  static async checkSupport(): Promise<WebAuthnSupport> {
    const support: WebAuthnSupport = {
      available: false,
      platform_authenticator: false,
      cross_platform_authenticator: false,
      user_verification: false,
      resident_key: false,
      conditional_mediation: false,
      large_blob: false
    };

    // Basic WebAuthn API support
    if (!window.PublicKeyCredential) {
      return support;
    }

    support.available = true;

    try {
      // Platform authenticator support
      if (PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
        support.platform_authenticator = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      }

      // Conditional mediation support (autofill)
      if (PublicKeyCredential.isConditionalMediationAvailable) {
        support.conditional_mediation = await PublicKeyCredential.isConditionalMediationAvailable();
      }

      // Check for other capabilities
      support.cross_platform_authenticator = true; // Generally supported if WebAuthn is available
      support.user_verification = true; // Generally supported
      support.resident_key = true; // Generally supported in modern browsers
      
      // Large blob extension support (experimental)
      support.large_blob = 'largeBlob' in PublicKeyCredential.prototype;
    } catch (error) {
      console.warn('Error checking WebAuthn capabilities:', error);
    }

    return support;
  }

  /**
   * Check if the current browser supports WebAuthn
   */
  static isSupported(): boolean {
    return !!(window.PublicKeyCredential && navigator.credentials && navigator.credentials.create);
  }

  /**
   * Get user agent information for logging
   */
  static getUserAgent(): string {
    return navigator.userAgent;
  }
}

// Credential options builders
export class CredentialOptionsBuilder {
  /**
   * Build registration options for creating a new passkey
   */
  static buildRegistrationOptions(
    request: PasskeyRegistrationRequest,
    challenge: ArrayBuffer,
    rpId: string,
    rpName: string,
    origin: string,
    excludeCredentials: string[] = []
  ): PublicKeyCredentialCreationOptionsExtended {
    const userId = Base64URL.fromString(request.user_id);
    
    return {
      rp: {
        id: rpId,
        name: rpName
      },
      user: {
        id: userId,
        name: request.username,
        displayName: request.display_name
      },
      challenge,
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' },  // ES256
        { alg: -257, type: 'public-key' } // RS256
      ],
      timeout: 300000, // 5 minutes
      attestation: 'none',
      authenticatorSelection: {
        authenticatorAttachment: request.authenticator_attachment,
        userVerification: request.user_verification || 'preferred',
        residentKey: request.resident_key || 'preferred',
        requireResidentKey: false
      },
      excludeCredentials: excludeCredentials.map(id => ({
        id: Base64URL.decode(id),
        type: 'public-key' as const,
        transports: ['internal', 'external', 'usb', 'nfc', 'ble'] as AuthenticatorTransport[]
      })),
      extensions: {
        credProps: true
      }
    };
  }

  /**
   * Build authentication options for passkey login
   */
  static buildAuthenticationOptions(
    request: PasskeyAuthenticationRequest,
    challenge: ArrayBuffer,
    rpId: string,
    allowCredentials: string[] = []
  ): PublicKeyCredentialRequestOptionsExtended {
    return {
      challenge,
      timeout: request.timeout || 60000, // Reduzido para 60 segundos
      rpId,
      userVerification: request.user_verification || 'preferred',
      allowCredentials: allowCredentials.length > 0 ? allowCredentials.map(id => ({
        id: Base64URL.decode(id),
        type: 'public-key' as const,
        transports: ['internal', 'external', 'usb', 'nfc', 'ble'] as AuthenticatorTransport[]
      })) : [],
      extensions: {}
    };
  }
}

// Response parsers
export class ResponseParser {
  /**
   * Parse registration response from browser
   */
  static parseRegistrationResponse(credential: PublicKeyCredential): PasskeyRegistrationResponse {
    const response = credential.response as AuthenticatorAttestationResponse;
    const clientExtensions = credential.getClientExtensionResults();
    
    return {
      credential_id: Base64URL.encode(credential.rawId),
      public_key: new Uint8Array(response.getPublicKey()!),
      attestation_object: new Uint8Array(response.attestationObject),
      client_data_json: new Uint8Array(response.clientDataJSON),
      transports: response.getTransports?.() as AuthenticatorTransport[],
      authenticator_attachment: credential.authenticatorAttachment as AuthenticatorAttachment,
      backup_eligible: clientExtensions.credProps?.rk,
      backup_state: false // Will be determined by authenticator data
    };
  }

  /**
   * Parse authentication response from browser
   */
  static parseAuthenticationResponse(credential: PublicKeyCredential): PasskeyAuthenticationResponse {
    const response = credential.response as AuthenticatorAssertionResponse;
    
    return {
      credential_id: Base64URL.encode(credential.rawId),
      authenticator_data: new Uint8Array(response.authenticatorData),
      client_data_json: new Uint8Array(response.clientDataJSON),
      signature: new Uint8Array(response.signature),
      user_handle: response.userHandle ? new Uint8Array(response.userHandle) : undefined
    };
  }
}

// Error handling utilities
export class WebAuthnErrorHandler {
  /**
   * Convert browser errors to our error format
   */
  static handleError(error: any): WebAuthnError {
    const errorType = this.getErrorType(error);
    
    return {
      name: errorType,
      message: this.getErrorMessage(errorType, error),
      code: error.code,
      details: {
        originalError: error.message,
        stack: error.stack
      }
    };
  }

  private static getErrorType(error: any): WebAuthnErrorType {
    if (error.name === 'NotSupportedError') return 'NotSupportedError';
    if (error.name === 'SecurityError') return 'SecurityError';
    if (error.name === 'AbortError') return 'AbortError';
    if (error.name === 'ConstraintError') return 'ConstraintError';
    if (error.name === 'NotAllowedError') return 'NotAllowedError';
    if (error.name === 'InvalidStateError') return 'InvalidStateError';
    if (error.name === 'NetworkError') return 'NetworkError';
    if (error.name === 'TimeoutError') return 'TimeoutError';
    return 'UnknownError';
  }

  private static getErrorMessage(type: WebAuthnErrorType, originalError: any): string {
    const messages: Record<WebAuthnErrorType, string> = {
      NotSupportedError: 'WebAuthn não é suportado neste navegador ou dispositivo.',
      SecurityError: 'Erro de segurança. Verifique se o site está sendo acessado via HTTPS.',
      AbortError: 'Operação cancelada pelo usuário.',
      ConstraintError: 'Restrições de autenticação não foram atendidas.',
      NotAllowedError: 'Operação não permitida. O usuário pode ter negado a solicitação.',
      InvalidStateError: 'Estado inválido. A credencial pode já existir.',
      NetworkError: 'Erro de rede durante a operação.',
      TimeoutError: 'Tempo limite excedido. Tente novamente.',
      UnknownError: 'Erro desconhecido durante a operação WebAuthn.'
    };

    return messages[type] || originalError.message || 'Erro desconhecido';
  }
}

// Validation utilities
export class ValidationUtils {
  /**
   * Validate origin matches expected value
   */
  static validateOrigin(clientDataJSON: ArrayBuffer, expectedOrigin: string): boolean {
    try {
      const clientData = JSON.parse(Base64URL.toString(clientDataJSON));
      return clientData.origin === expectedOrigin;
    } catch {
      return false;
    }
  }

  /**
   * Validate challenge matches expected value
   */
  static validateChallenge(clientDataJSON: ArrayBuffer, expectedChallenge: string): boolean {
    try {
      const clientData = JSON.parse(Base64URL.toString(clientDataJSON));
      return clientData.challenge === expectedChallenge;
    } catch {
      return false;
    }
  }

  /**
   * Validate credential ID format
   */
  static validateCredentialId(credentialId: string): boolean {
    try {
      // Should be valid base64url and reasonable length
      const decoded = Base64URL.decode(credentialId);
      return decoded.byteLength >= 16 && decoded.byteLength <= 1024;
    } catch {
      return false;
    }
  }

  /**
   * Validate user ID format
   */
  static validateUserId(userId: string): boolean {
    // Should be a valid UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(userId);
  }
}

// Utility functions for common operations
export const WebAuthnUtils = {
  Base64URL,
  ChallengeManager,
  WebAuthnDetector,
  CredentialOptionsBuilder,
  ResponseParser,
  WebAuthnErrorHandler,
  ValidationUtils
};

export default WebAuthnUtils;